import { siteConfig } from "@acme/config";
import { CustomMDX } from "@acme/mdx";
import { imageCdnFullWidthProps } from "@acme/ui/image-cdn";
import { formatDate } from "@acme/utils";
import { Link } from "@tanstack/react-router";
import { Image } from "@unpic/react";
import type { ReactNode } from "react";
import { ArticleMeta } from "~/components/blog/article-card";
import { ArticleToc } from "~/components/blog/article-toc";
import { Disclosure } from "~/components/inside/disclosure";
import { NewTab } from "~/components/inside/new-tab";
import { getCtaConfig } from "~/lib/constants/navbar";
import { mdxLocaleProps } from "~/lib/mdx-locale";
import type { PublicArticle } from "~/lib/server/blog";
import * as m from "~/paraglide/messages.js";
import { getLocale, localizeHref } from "~/paraglide/runtime.js";

/**
 * The article page body. The public route and the reviewer preview both render
 * this, so what a reviewer approves is exactly what a reader will see.
 *
 * Component labels inside the article take the language of the content
 * actually served: an untranslated piece under /lv/ is English, and so are its
 * labels. Their links follow the reader's site, so they stay under /lv/.
 */
export function ArticleView({
  actions,
  article,
  banner,
  children,
}: Readonly<{
  /** Like and share, on the public page only. */
  actions?: ReactNode;
  article: PublicArticle;
  /** Shown above the header, e.g. the preview notice. */
  banner?: ReactNode;
  /** Rendered after the article, e.g. related reading. */
  children?: ReactNode;
}>) {
  const published = article.publishedAt ?? article.createdAt;
  const sources = article.sources ?? null;
  // The Sources heading is folded under the toggle below, so it is not a
  // landmark of its own in the index.
  const toc = (article.toc ?? []).filter(
    (item) =>
      !(
        sources &&
        item.depth === 2 &&
        item.title.toLowerCase() === sources.heading.toLowerCase()
      )
  );

  return (
    <div className="wrap">
      {banner}
      {/* Centred, and with no scene band: a reader who has already chosen the
          piece wants its first sentence, not a stage. */}
      <header className="article-header">
        <Link className="back-link" to="/blog">
          <span aria-hidden="true">←</span> {m.blog_back()}
        </Link>

        <ArticleMeta article={article} />
        <h1>{article.title}</h1>
        {article.description ? (
          <p className="article-deck">{article.description}</p>
        ) : null}

        <p className="author-line">
          <strong>{article.author?.name ?? siteConfig.name}</strong>
          {published ? (
            <time dateTime={published.toISOString()}>
              {formatDate(published, getLocale())}
            </time>
          ) : null}
        </p>
      </header>

      {article.imageUrl ? (
        <div className="article-cover">
          <div className="editorial-art">
            <Image
              alt={article.imageAlt ?? ""}
              fetchPriority="high"
              layout="fullWidth"
              loading="eager"
              sizes="(max-width: 720px) 100vw, 1000px"
              src={article.imageUrl}
              {...imageCdnFullWidthProps(article.imageUrl)}
            />
          </div>
        </div>
      ) : null}

      <div className="article-layout">
        <ArticleToc
          end={{ label: m.blog_toc_end(), to: "/how-we-work" }}
          title={m.blog_toc_title()}
          toc={toc}
        />

        <article className="article-body mdx-content">
          <CustomMDX
            html={article.contentHtml ?? ""}
            {...mdxLocaleProps(article, (href) => localizeHref(href))}
          />

          {sources ? (
            <div className="article-sources">
              <Disclosure
                id="sources"
                summary={`${sources.heading} (${sources.count})`}
              >
                <CustomMDX
                  className="sources-list"
                  html={sources.html}
                  {...mdxLocaleProps(article, (href) => localizeHref(href))}
                />
              </Disclosure>
            </div>
          ) : null}

          <div className="article-end">
            <p>{m.blog_end_question()}</p>
            <a href={getCtaConfig().href} rel="noopener" target="_blank">
              {m.blog_end_action()}
              <NewTab />
            </a>
          </div>

          {actions}
        </article>
      </div>

      {children}
    </div>
  );
}
